<template>
  <section
    v-view
    class="block block-banner herotop"
    :class="[
      fallbackSize,
      {
        '-inline-media': model.InlineMedia,
        '-fadingblock': model.FadeInMode,
        '-image-ready': imageReady,
      },
    ]"
  >
    <!-- Adds gradient to the whole banner. -->
    <div v-if="showOverlay" class="overlay-image" :class="overlayDir" />
    <section
      class="herotop-media-wrapper -p-relative"
      :class="{ grid: model.InlineMedia }"
    >
      <div :class="inlineMediaPos">
        <FocalImage
          v-if="model.Media && model.Media.__type__ === 'ImageMediaData'"
          :imageObject="model.Media"
        />
        <video
          v-else-if="model.Media && model.Media.__type__ === 'VideoFile'"
          class="w-100 d-block"
          preload="auto"
          autoplay="autoplay"
          playsinline
          muted
          loop
          :src="model.Media.Url"
        ></video>
        <SliderFormatData
          v-else
          :model="model.ImageCollectorMaxBlock"
          :sizes="'100vw'"
          :autoProportion="false"
          :proportions="bannerProportions[model.BannerHeight]"
        >
          <template v-slot="{ proportion, slides }">
            <MediaCarousel
              :slides="slides"
              :proportion="proportion"
              :tooltip="false"
              :animated="model.AnimatedSlider"
              :disableLazy="fallbackSize !== ''"
              :containToFullHeight="model.BannerHeight !== 0"
              :cover="model.BannerHeight !== 0"
              :navPlacement="$mqAliases.desktop ? '36px' : '18px'"
              :navLargeHitAreas="true"
              @ready="imageReady = true"
            />
          </template>
        </SliderFormatData>
        <!-- svg component overlay -->
        <component
          v-if="svgComponent && imageReady"
          :is="svgComponent"
          :color="fontColor"
          class="overlay-svg-component"
        />
        <div v-else-if="model.SvgOverlay" class="overlay-svg" :class="svgClass">
          <div
            :class="{
              'd-flex justify-content-end':
                model.SvgPlacement === 1 || model.SvgPlacement === 3,
            }"
          >
            <object :data="model.SvgOverlay" type="image/svg+xml">
              <img :src="model.SvgOverlay" />
            </object>
          </div>
        </div>
      </div>
    </section>
    <div
      class="overlay-text"
      :class="ypos"
      style="z-index: 10001; pointer-events: none"
    >
      <div class="grid">
        <div class="g-8 g-md-6" v-if="CSRHeroStyle">
          <CSRLogo class="m-b-18 m-t-12 m-t-md-0" :color="fontColor" />
        </div>
        <div class="g-12" :class="xpos">
          <component
            v-if="model.Title"
            :is="h1Title ? 'h1' : 'h2'"
            class="h2 p-b-9 m-b-0"
            :class="{
              'p-b-21': CSRHeroStyle,
              'g-md-6': spanText,
              '-activity-style': activityStyle,
              '-campaign-style': campaignStyle,
            }"
            v-html="title"
            :style="style"
          />
          <div
            v-if="model.Lead"
            class="block-banner-lead g-md-6"
            :class="{
              'g-md-6 -span-text': spanText,
              '-campaign-style': campaignStyle,
            }"
            :style="style"
          >
            <div v-html="model.Lead" />
            <ButtonCollection
              v-if="model.Buttons && model.Buttons[0] && spanText"
              class="m-t-9 m-t-md-9 m-t-lg-15"
              :class="{ 'p-t-6': model.Buttons[0].Style !== 3 }"
              :model="model.Buttons"
              :small="!$mqAliases.desktop"
              :color="{
                FontColor: fontColor,
                BackGroundColor: backgroundColor,
              }"
              :tracking="{ id: model.__id__, pos: index, title: title }"
              style="pointer-events: all"
            />
          </div>
          <ButtonCollection
            v-if="model.Buttons && model.Buttons[0] && !spanText"
            :class="{ 'p-t-15 p-t-md-18': model.Buttons[0].Style !== 3 }"
            :model="model.Buttons"
            :small="!$mqAliases.desktop"
            :color="{
              FontColor: fontColor,
              BackGroundColor: backgroundColor,
            }"
            :tracking="{ id: model.__id__, pos: index, title: title }"
            style="pointer-events: all"
          />
          <div v-else-if="!spanText" class="m-b-18" />
        </div>
      </div>
    </div>
    <div v-if="countDownDate" class="countdown-area">
      <CountDownTo :value="countDownDate" class="" />
    </div>
  </section>
</template>
<script>
import SliderFormatData from '@/Scripts/components/shared/GenericSlider/Assets/SliderFormatData.vue';
import { PROPORTIONS } from '@/Scripts/constants';
import imgSrcMixin from '@/Scripts/lib/mixins/imgSrcMixin';
import TinyColor from 'tinycolor2';
import { mapState } from 'vuex';
import { parseJSONDate } from '@/Scripts/lib/helper/dateHelper.js';
const CountDownTo = () => import('@/Scripts/components/shared/CountDownTo/CountDownTo.vue');
const ButtonCollection = () => import('@/Scripts/components/shared/ButtonCollection/ButtonCollection.vue');
const FocalImage = () => import('@/Scripts/components/media/Image/FocalImage.vue');
const MediaCarousel = () => import('@/Scripts/components/media/MediaCarousel/MediaCarousel.vue');
const CSRChart = () => import('@/Scripts/components/shared/svg/Campaigns/CSRchart.vue');
const CSRLogo = () => import('@/Scripts/components/layout/logo/CSRLogo.vue');

export default {
  name: 'HeroBlock',
  mixins: [imgSrcMixin],
  components: {
    MediaCarousel,
    ButtonCollection,
    SliderFormatData,
    FocalImage,
    CSRChart,
    CSRLogo,
    CountDownTo
  },
  props: {
    model: Object,
    index: Number,
    h1Title: Boolean
  },
  data () {
    return {
      yPositions: ['-top', '-top', '-bottom', '-bottom', '-top'],
      xPositions: [
        'grid-md-4 g-lg-4',
        'g-md-9-4 g-lg-9-4',
        'grid-md-4 g-lg-4',
        'g-md-9-4 g-lg-9-4',
        'grid-12'
      ],
      xPositionsHalf: [
        'grid-md-6',
        'g-md-7-6',
        'grid-md-6',
        'g-md-7-6',
        'grid-12'
      ],
      overlayClasses: ['-top', '-top', '-bottom', '-left', '-right', '-no-gradient-dark'],
      bannerProportions: [PROPORTIONS.DEFAULT, PROPORTIONS.MEDIUM, PROPORTIONS.SMALL, PROPORTIONS.XSMALL],
      bannerFallBackSize: ['', '-md', '-sm', '-xs'],
      svgPosition: ['-topleft grid', '-topright grid', '-bottomleft grid', '-bottomright grid', '-span'],
      imageReady: false
    };
  },
  methods: {
    hasVideo (slides) {
      return slides.filter(obj => obj.type === 'VideoFile').length > 0;
    }
  },
  computed: {
    ...mapState({
      blackTheme: state => state.appContext.blackTheme
    }),
    countDownDate () {
      if (!this.model || !this.model.CountDownTo) {
        return null;
      }
      const d = parseJSONDate(this.model.CountDownTo);
      if (new Date(d).getFullYear() < 2000) {
        return null;
      }
      return d;
    },
    title () {
      if (this.model.Subtitle) {
        const devider = '<br />';
        return `<span class='-bold'>${this.model.Title}</span>${devider}<span class='-thin'>${this.model.Subtitle}</span>`;
      }
      return this.model.Title;
    },
    showOverlay () {
      if ((this.$mqAliases.xs || this.$mqAliases.sm) && this.model.MobilePlacement === 0) {
        return false;
      }
      return this.model.Overlay || this.model.OverlayDirection > 0;
    },
    fallbackSize () {
      return this.bannerFallBackSize[this.model.BannerHeight];
    },

    inlineMediaPos () {
      if (this.model.InlineMedia) {
        if (this.spanText || this.$mqAliases.xs || this.$mqAliases.sm) {
          return 'g-12';
        }
        if (this.halfWidth) {
          if (this.model.Placement === 1 || this.model.Placement === 3) {
            return 'g-6';
          } else {
            return 'g-7-6';
          }
        } else {
          if (this.model.Placement === 1 || this.model.Placement === 3) {
            return 'g-8';
          } else {
            return 'g-5-8';
          }
        }
      }
      return '';
    },
    spanText () {
      return this.model.Placement === 4;
    },
    halfWidth () {
      return this.model.HalfWidth || this.spanText;
    },
    activityStyle () {
      return this.model.HeroStyle === 1;
    },
    campaignStyle () {
      return this.model.HeroStyle === 2 || this.CSRCampaignStyle;
    },
    CSRCampaignStyle () {
      return this.model.HeroStyle === 3;
    },
    CSRHeroStyle () {
      return this.model.HeroStyle === 5;
    },
    svgComponent () {
      return this.CSRCampaignStyle ? 'CSRChart' : null;
    },
    svgClass () {
      // special svgView
      if (this.svgComponent) {
        return '-span';
      }
      // no positioning
      if (typeof this.model.SvgPlacement === 'undefined' || this.model.SvgPlacement === null || this.model.SvgPlacement === 4) {
        return '-span';
      }
      // positioning
      return this.svgPosition[this.model.SvgPlacement];
    },
    ypos () {
      if (this.$mqAliases.xs || this.$mqAliases.sm) {
        return {
          0: '',
          1: '-top',
          2: '-bottom'
        }[this.model.MobilePlacement] || '';
      }
      return this.yPositions[this.model.Placement];
    },
    xpos () {
      if (this.$mqAliases.xs || this.$mqAliases.sm) {
        return this.model.MobilePlacement > 0 ? this.xPositions[0] : '';
      }
      if (this.fallbackSize === '-xs') {
        return '';
      }
      if (this.CSRHeroStyle) {
        return 'g-md-6';
      }
      if (this.halfWidth) {
        return this.xPositionsHalf[this.model.Placement];
      }
      return this.xPositions[this.model.Placement];
    },
    backgroundColor () {
      return this.model.ColorOptions.GradientTo || this.model.ColorOptions.BackgroundColor || (this.blackTheme ? this.Globals.colors.black : this.Globals.colors.white) || this.Globals.colors.white;
    },
    fontColor () {
      if ((this.$mqAliases.xs || this.$mqAliases.sm) && this.model.MobilePlacement === 0) {
        return TinyColor.mostReadable(this.backgroundColor, [this.Globals.colors.white, this.Globals.colors.black]).toHexString();
      }
      return this.model.ColorOptions.FontColor;
    },
    style () {
      return {
        color: `${this.fontColor}`,
        borderColor: `${this.fontColor}`
      };
    },
    overlayDir () {
      return this.overlayClasses[this.model.OverlayDirection];
    }

  },
  mounted () {
    this.$nextTick(() => {
      this.$emit('ready');
    });
  }
};
</script>
<style lang="scss" src="@/scss/components/components.block.scss"></style>
<style lang="scss" src="@/scss/components/components.block-banner.scss"></style>
<style lang="scss" src="@/scss/components/components.overlay-image.scss"></style>

<style lang="scss">
@import "@/scss/defaults.scss";
.herotop {
  padding: 0 !important;
  position: relative;
  @include media-breakpoint-down(sm) {
    h1.h2,
    h2.h2 {
      @include font-size(larger, xs);
      padding-bottom: 6px !important;
    }
    .block-banner-lead,
    .block-banner-lead p {
      @include font-size(body);
    }
  }
  .herotop-media-wrapper {
    min-height: 20vw;
    aspect-ratio: 4 / 5;
    // fallback
    @supports not (aspect-ratio: auto) {
      padding-top: 125%;
      height: 0;
      position: relative;
      overflow: hidden;
    }
    @include media-breakpoint-up(md) {
      aspect-ratio: 16 / 9;
      @supports not (aspect-ratio: auto) {
        padding-top: 56.25%;
        height: 0;
        position: relative;
        overflow: hidden;
      }
    }
  }
  &.-inline-media,
  &.-image-ready {
    .herotop-media-wrapper {
      aspect-ratio: unset;
      @supports not (aspect-ratio: auto) {
        padding-top: 0;
        height: auto;
        overflow: auto;
      }
    }
  }
  &.-fadingblock {
    .herotop-media-wrapper {
      opacity: 1;
      transition: opacity 2s;
      animation: 2s ease-out 0s 1 fadeIn;
    }

    &:not(.view-in) {
      .herotop-media-wrapper {
        opacity: 0;
      }
    }
  }
  &.-inline-media {
    .herotop-media-wrapper {
      padding-top: 18px;
      padding-bottom: 18px;
      @include media-breakpoint-up(lg) {
        padding-top: 54px;
        padding-bottom: 60px;
      }
    }
  }
  .overlay-image {
    z-index: 100;
    pointer-events: none;
    &.-no-gradient-dark {
      background: none;
      background-color: rgba(29, 29, 29, 0.5);
    }
  }

  @include media-breakpoint-up(md) {
    &.-md,
    &.-sm,
    &.-xs {
      overflow: hidden;
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      .herotop-media-wrapper {
        min-height: 0;
        min-width: 100%;
      }
    }

    &.-md {
      height: 400px;
    }

    &.-sm {
      height: 300px;
    }

    &.-xs {
      height: 200px;
      h2 {
        @include font-responsive(larger);
      }
      .block-banner-lead,
      p {
        @include font-responsive(body);
      }
      .-top {
        top: 36px !important;
      }
    }
  }
  .overlay-svg {
    position: absolute;
    z-index: 10000;
    pointer-events: none;

    &.-span {
      top: 0;
      width: 100%;
      height: 100%;
      > div,
      object,
      img {
        width: 100%;
        height: 100%;
      }
    }
    &:not(.-span) {
      @include media-breakpoint-down(md) {
        object,
        img {
          transform: scale(0.75);
        }
      }
    }

    &.-topleft,
    &.-topright {
      top: 0;
      left: 0;
      right: 0;
      padding-top: 18px;
      @include media-breakpoint-up(lg) {
        padding-top: 54px;
      }
    }
    &.-bottomleft,
    &.-bottomright {
      bottom: 0;
      left: 0;
      right: 0;
      padding-top: 36px;
      @include media-breakpoint-up(lg) {
        padding-top: 60px;
      }
    }
  }
  .overlay-svg-component {
    position: absolute;
    overflow: visible;
    width: 100%;
    height: auto;
    bottom: 18px;
    padding: 0 18px;
    z-index: 1;
    @include media-breakpoint-up(md) {
      width: 40%;
      left: 50%;
      padding: 0;
      transform: translateX(-50%);
      bottom: 36px;
    }
    @include media-breakpoint-up(lg) {
      bottom: 42px;
    }
    @include media-breakpoint-up(xl) {
      width: 50%;
      bottom: 60px;
    }
  }
  .overlay-text {
    margin-top: 18px;
    .grid {
      padding-top: 0;
      padding-bottom: 0;
    }
    .block-banner-lead {
      padding-bottom: 0;
      @include media-breakpoint-up(lg) {
        padding-bottom: 6px;
      }

      &.-csrhero-style {
        p {
          @include font-responsive(body);
        }
      }
    }
    h2 {
      margin-top: 0 !important;
    }

    p {
      margin-bottom: 15px !important;
      &:last-of-type {
        margin-bottom: 0 !important;
      }
    }
    &.-top,
    &.-bottom {
      margin-top: 0;
      position: absolute;
      width: 100%;

      @include media-breakpoint-down(md) {
        .block-banner-lead {
          @include font-size(body, xs);
          p:last-of-type {
            margin-bottom: 0 !important;
          }
        }
        .button-collection {
          .btn-v2 button,
          .btn-v2 a {
            @include font-size(small, xs);
          }
        }
      }
      .block-banner-lead {
        padding-bottom: 0;
      }
    }
    h2.-activity-style {
      text-transform: lowercase;
      font-variant: small-caps !important;
    }
    h2.-campaign-style {
      @include media-breakpoint-up(md) {
        @include font-size(large, xs);
      }
      @include media-breakpoint-up(lg) {
        @include font-size(xl, md);
      }
    }
    h2.-csrhero-style {
      @include media-breakpoint-up(md) {
        @include font-size(larger, xs);
      }
      @include media-breakpoint-up(lg) {
        @include font-size(larger, md);
      }
    }

    &.-top {
      width: 100%;
      top: 18px;
      @include media-breakpoint-up(lg) {
        top: 54px;
      }
    }

    &.-bottom {
      width: 100%;
      bottom: 0;
      @include media-breakpoint-up(md) {
        bottom: 36px;
      }
      @include media-breakpoint-up(lg) {
        bottom: 60px;
      }
    }
  }
  .block-banner-lead.-span-text,
  .block-banner-lead.-span-text p {
    @include font-size(body);
    @include media-breakpoint-up(md) {
      @include font-size(larger, md);
    }
    @include media-breakpoint-up(lg) {
      @include font-size(xl, md);
    }
  }
  .block-banner-lead.-campaign-style,
  .block-banner-lead.-campaign-style p {
    @include font-size(body);
    @include media-breakpoint-up(md) {
      @include font-size(body, xs);
    }
    @include media-breakpoint-up(lg) {
      @include font-size(large, md);
    }
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .countdown-area {
    display: flex;
    justify-content: center;
    position: absolute;
    width: 100%;
    bottom: 36px;
    .countdownto {
    }
  }
}
</style>
